// Three.js:
import * as THREE from 'three';

// Global:
import { getActiveLayerUID, getColorScheme, getGlobalState, getLayersState } from '../Gui/store/persistent/persistentStore.js';

// Custom:
import Grid from '../Geodata/grid.js';
import { LayerDraw } from './LayerDraw.js';
import { VisualizationElement, VisualizationElements } from './VisualizationElements.js';

// Store Zustand
import { PipelineExecutionResult } from '@data/Simulations/pipeline/Pipeline.js';
import FVector from '@data/geometry/FVector';
import { ColorScheme, MultiModeOptions, SIM_VVA } from '@visualizer/types';
import { InternalMixerElement } from 'Simulations/LayerMixed.js';
import { safeMapValue } from 'Utils/UtilityFunctions.js';
import { getElement } from '../Gui/store/persistent/persistentStore.js';
import { DEFAULT_ELEMENT, resolveSensorColorMode } from '../Gui/store/layerState.js';
import { getGrid } from 'store/LoadingStore';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { VisualizationElementId } from '@rendering/types/VisualizationElements';

// Density
import { getDensityRenderScale } from '@data/Simulations/pipeline/blocks/DensityBlock.js';

type LayerDrawOptions = {
    layerDraw: LayerDraw;
    internalMixerElement: InternalMixerElement;
};
export class InstanceRenderer {
    private sensorCount: number;
    initialized: boolean;

    private multiDraw: LayerDrawOptions[] = [];
    private singleDraw: LayerDraw;

    private isMultimode: boolean;
    private directionlessVisualizationElementId: VisualizationElementId;
    private lastSinglePipelineResult?: PipelineExecutionResult;

    private pickableSensorIndices: Set<number> = new Set();
    constructor() {
        this.sensorCount = Grid.sensorPointsN;

        const elementState = getElement() ?? DEFAULT_ELEMENT;
        this.singleDraw = new LayerDraw(VisualizationElements.getVisualizationElement(elementState.SEL_VIS_ELEMENT));
        this.initialized = false;
        this.isMultimode = false;
        this.directionlessVisualizationElementId = VisualizationElementId.FlatVoxel;

        // Initialize visibility:
        const globalState = getGlobalState();
        const showSensors = globalState.context.showGrid;
        this.toggleSensorsSingle(showSensors);

        const layersState = getLayersState();
        const showZeroPoints = layersState.layerStates[layersState.activeLayerID]?.element?.SHOW_ZERO_VALUE_POINTS;
        this.toggleBlackPoints(showZeroPoints);
    }

    public getSingleDraw() {
        return this.singleDraw;
    }

    public getPickableSensorMeshes(): THREE.Object3D[] {
        if (this.isMultimode) {
            return this.multiDraw.flatMap((entry) => entry.layerDraw.getPickableSensorMeshes());
        }
        return this.singleDraw.getPickableSensorMeshes();
    }

    public getPickableSensorIndices(): ReadonlySet<number> {
        return this.pickableSensorIndices;
    }

    public updateMeshGeometry(element: VisualizationElement) {
        this.singleDraw.updateGeometry(element);
        if (!this.isMultimode && this.lastSinglePipelineResult) {
            this.pickableSensorIndices.clear();
            this.drawSensors(this.lastSinglePipelineResult, this.singleDraw);
        }
        console.log(this.multiDraw);
    }

    public enableMultimode(elements: InternalMixerElement[]) {
        this.disposeMultimode();
        this.isMultimode = true;
        this.singleDraw.hide();
        elements.forEach((element) => {
            this.addLayerDraw(element);
        });

        this.updateFrames();
    }

    public addLayerDraw(element: InternalMixerElement) {
        // Add layers that are not already in:
        const existingEntry = this.multiDraw.find((entry) => entry.internalMixerElement.layerId === element.layerId);
        if (existingEntry) {
            existingEntry.internalMixerElement = element;
            return;
        }

        const selectedVisElement = getLayersState().layerStates[element.layerId].element.SEL_VIS_ELEMENT;
        const visualizationElement = VisualizationElements.getVisualizationElement(selectedVisElement);
        const layerDraw = new LayerDraw(visualizationElement);
        this.multiDraw.push({
            layerDraw,
            internalMixerElement: element,
        });
    }

    public removeLayerDraw(layerId: string) {
        const idx = this.multiDraw.findIndex((e) => e.internalMixerElement.layerId === layerId);
        if (idx === -1) return;

        this.multiDraw[idx].layerDraw.dispose();
        this.multiDraw.splice(idx, 1);
    }

    public disableMultimode() {
        this.disposeMultimode();
        this.singleDraw.show();

        this.isMultimode = false;
        this.updateFrames();
    }

    public updateFrames(pipelineResult?: PipelineExecutionResult) {
        this.pickableSensorIndices.clear();
        if (this.isMultimode) {
            ControllerManager.getInstance().getMixerController().getMixerLayer().applyMultimodeFilter();

            this.multiDraw.forEach((layerOpt) => {
                const pipelineResult = layerOpt.internalMixerElement.updatedLayerPipelineResult;
                this.drawSensors(
                    pipelineResult,
                    layerOpt.layerDraw,
                    layerOpt.internalMixerElement.frameDrawOptions,
                    layerOpt.internalMixerElement.layerId
                );
            });
        } else {
            if (!pipelineResult) return;
            this.lastSinglePipelineResult = pipelineResult;
            this.drawSensors(pipelineResult, this.singleDraw);
        }
    }

    public toggleBlackPoints(showBlackPoints: boolean) {
        const showSensors = getGlobalState().context.showGrid;
        this.singleDraw.showBlackDots(showSensors && showBlackPoints);
    }

    public toggleSensorsSingle(show: boolean) {
        this.singleDraw.showSensors(show);
    }

    public syncContextVisibility(showGrid: boolean, showZeroValues: boolean) {
        if (this.isMultimode) {
            this.updateFrames();
            return;
        }

        this.toggleSensorsSingle(showGrid);
        this.toggleBlackPoints(showGrid && showZeroValues);
    }

    private drawSensors(
        pipelineResult: PipelineExecutionResult,
        layerDraw: LayerDraw,
        multimodeOptions?: MultiModeOptions,
        layerId?: string
    ) {
        const isMultimode = this.isMultimode;
        const sensorDatas = pipelineResult.data;
        const totalSensorNumber = this.sensorCount;
        const activeElementState = getElement() ?? DEFAULT_ELEMENT;
        const multiElementState = layerId ? (getLayersState().layerStates[layerId]?.element ?? DEFAULT_ELEMENT) : DEFAULT_ELEMENT;
        const elementState = isMultimode ? multiElementState : activeElementState;
        const densityScale = getDensityRenderScale(elementState.ELEMENT_CURRENT_DENSITY);
        const sensorColorMode = resolveSensorColorMode(elementState);
        const showContextSensors = getGlobalState().context.showGrid;
        const showZeroValuePoints = getLayersState().layerStates[getLayersState().activeLayerID]?.element?.SHOW_ZERO_VALUE_POINTS ?? false;
        layerDraw.setNoStrokes(Boolean(elementState.NO_STROKES));

        const uniforms = {
            spin2: elementState.ELEMENT_SPIN == 1,
            multi: isMultimode,
            opacity: multimodeOptions?.transparencyAmount,
            transparent: multimodeOptions?.transparent,
        };
        layerDraw.updateMaterialUniforms(uniforms);

        if (isMultimode && multimodeOptions) {
            const showLayerSensors = showContextSensors && multimodeOptions.showSensors;
            layerDraw.makeTransparent(multimodeOptions.transparent, multimodeOptions.transparencyAmount);
            layerDraw.showSensors(showLayerSensors);
            layerDraw.showBlackDots(showLayerSensors && showZeroValuePoints);
        }

        // HERE UPDATE BLACK POINTS
        const rawPositions: FVector[] = new Array(totalSensorNumber).fill(new FVector(Number.MIN_SAFE_INTEGER, 0, 0));
        sensorDatas
            .filter((sensorData) => sensorData.getScalarValue() == 0)
            .forEach((sensorData) => {
                const id = sensorData.getID();
                const position = sensorData.getPosition();
                rawPositions[id] = new FVector(position.x, position.y, position.z);
            });

        const blackDotPositions = rawPositions.flatMap((pos) => pos.toArray());
        const size = (5 * this.computeSensorScale(densityScale, 1, true, 0, 0)) / Grid.gridRes;

        layerDraw.updateBlackDots(blackDotPositions, size);

        // NOW UPDATE SENSORS
        layerDraw.resetScale();
        layerDraw.setDirectionlessVisualizationElementId(this.directionlessVisualizationElementId);
        const visualizationElementId = VisualizationElements.getVisualizationElement(elementState.SEL_VIS_ELEMENT).id;

        // Check layerKind (use activeLayer if not in multi):
        const resolvedLayerId = layerId ?? getActiveLayerUID();
        let isFreeViewLayer = false;
        if (resolvedLayerId) isFreeViewLayer = getGrid().searchLayerByUID(resolvedLayerId).isOfType(SIM_VVA);

        // Use directionless only in FreeView kind:
        const allowDirectionless =
            isFreeViewLayer &&
            (visualizationElementId === VisualizationElementId.Quad || visualizationElementId === VisualizationElementId.Box);
        const directionlessItems: { position: FVector; color: THREE.Color; scale: number }[] = [];

        sensorDatas
            .filter((sensorData) => sensorData.getScalarValue() !== 0)
            .forEach((sensorData) => {
                const id = sensorData.getID();
                const mapMinValue = pipelineResult.executionContext.remappedMinMax[0];
                const mapMaxValue = pipelineResult.executionContext.remappedMinMax[1];
                const direction = sensorData.getVectorialResult();
                const dirEpsilon = 1e-5;
                const isDirectionless =
                    direction.mag() <= dirEpsilon && (sensorData.getScalarValue() === 1 || sensorData.getScalarValue() === 100);
                const color = isMultimode
                    ? new THREE.Color(multimodeOptions?.sensorsMultiColor)
                    : this.computeSensorColor(sensorData.getScalarValue(), mapMinValue, mapMaxValue, getColorScheme(), sensorColorMode);

                const scale = this.computeSensorScale(
                    densityScale,
                    elementState.scaleFactor,
                    elementState.normalized,
                    sensorData.getScalarValue(),
                    mapMaxValue
                );
                if (allowDirectionless && isDirectionless && sensorData.getScalarValue() > 0) {
                    const sensorIndex = sensorData.getIndex();
                    if (typeof sensorIndex === 'number' && sensorIndex >= 0) this.pickableSensorIndices.add(sensorIndex);

                    directionlessItems.push({ position: sensorData.getPosition(), color, scale });
                    return;
                }
                const sensorIndex = sensorData.getIndex();
                if (typeof sensorIndex === 'number' && sensorIndex >= 0) this.pickableSensorIndices.add(sensorIndex);

                layerDraw.updateAttributes(id, direction, color, scale);
            });
        layerDraw.updateDirectionlessInstances(allowDirectionless ? directionlessItems : []);
    }

    private computeSensorColor(
        value: number,
        minValue: number,
        maxValue: number,
        colorScheme: ColorScheme,
        sensorColorMode: 0 | 1 | 2
    ): THREE.Color {
        const minHue = colorScheme.GRID_FILL_H[0] / 100;
        const maxHue = colorScheme.GRID_FILL_H[1] / 100;
        const s = colorScheme.GRID_FILL_S;
        const l = colorScheme.GRID_FILL_L;
        const minLum = 0.2;
        const maxLum = 0.85;
        const allGreyLum = (minLum + maxLum) / 2;

        const outputColor = new THREE.Color();
        if (sensorColorMode === 2) {
            outputColor.setHSL(0, 0, allGreyLum);
        } else if (value < minValue || value > maxValue) {
            outputColor.setHSL(0, 0, 0.5);
        } else if (sensorColorMode === 1) {
            let lum = safeMapValue(value, minValue, maxValue, maxLum, minLum);
            lum = Math.min(1, Math.max(0, lum));
            outputColor.setHSL(0, 0, lum);
        } else {
            let hue = safeMapValue(value, minValue, maxValue, maxHue, minHue);
            if (hue < 0.01) hue = 0.01;
            if (hue > 0.61) hue = 0.61;
            outputColor.setHSL(hue, s, l);
        }
        return outputColor;
    }

    private computeSensorScale(densityScale: number, scaleFactor: number, normalized: boolean, sensorValue: number, maxValue: number) {
        const baseScale = Grid.gridRes;
        const scaleNormalized = normalized || maxValue === 0 ? 1 : sensorValue / maxValue;
        return baseScale * scaleFactor * densityScale * scaleNormalized;
    }

    private disposeMultimode() {
        this.multiDraw.forEach((layerDrawOpt) => layerDrawOpt.layerDraw.dispose());
        this.multiDraw = [];
    }
    public dispose() {
        this.singleDraw.dispose();
        this.disposeMultimode();
    }
}
